(() => {
	const { Autoplay } = window.ThemeCarousel.plugins;

	const getSlideElements = (viewport, emblaApi) => {
		const container = viewport.querySelector(".carousel__container");
		if (!container) return [];

		const loopNodes = emblaApi?.slideNodes();
		if (loopNodes?.length) return loopNodes;

		return Array.from(container.children);
	};

	const getSizedTargets = (viewport, emblaApi) => {
		const container = viewport.querySelector(".carousel__container");
		const targets = new Set();

		Array.from(container?.children ?? []).forEach((node) => targets.add(node));
		getSlideElements(viewport, emblaApi).forEach((node) => targets.add(node));

		return [...targets];
	};

	const applySlideSizes = (viewport, emblaApi) => {
		const width = Math.round(viewport.getBoundingClientRect().width);
		if (width <= 0) return 0;

		viewport.style.setProperty("--slideshow-slide-size", `${width}px`);

		getSizedTargets(viewport, emblaApi).forEach((slide) => {
			slide.style.flex = `0 0 ${width}px`;
			slide.style.width = `${width}px`;
			slide.style.minWidth = `${width}px`;
			slide.style.maxWidth = `${width}px`;
		});

		return width;
	};

	const bindSlideSizeSync = (viewport, emblaApi) => {
		let lastWidth = 0;
		let resizeFrame = 0;

		const syncSizes = (shouldReInit) => {
			const width = applySlideSizes(viewport, emblaApi);
			if (!width) return;

			const widthChanged = width !== lastWidth;
			lastWidth = width;

			if (shouldReInit && widthChanged) {
				emblaApi.reInit();
				applySlideSizes(viewport, emblaApi);
			}
		};

		const scheduleResize = () => {
			cancelAnimationFrame(resizeFrame);
			resizeFrame = requestAnimationFrame(() => syncSizes(true));
		};

		emblaApi.on("resize", scheduleResize);

		viewport.querySelectorAll("img").forEach((image) => {
			if (image.complete) return;
			image.addEventListener("load", scheduleResize, { once: true });
			image.addEventListener("error", scheduleResize, { once: true });
		});

		return () => {
			cancelAnimationFrame(resizeFrame);
			emblaApi.off("resize", scheduleResize);
		};
	};

	const initSlideshow = (root) => {
		const slideshows = root.querySelectorAll
			? root.querySelectorAll(".slideshow__carousel")
			: [root].filter((node) => node?.classList?.contains("slideshow__carousel"));

		slideshows.forEach((slideshow) => {
			const viewport = slideshow.querySelector(".carousel__viewport");
			if (!viewport) return;

			if (slideshow.classList.contains("is-initialized")) return;

			let slideSizeCleanup = null;

			const autoplayEnabled =
				slideshow.getAttribute("data-autoplay") === "true";
			const delay =
				Number.parseInt(slideshow.getAttribute("data-slide-duration"), 10) *
					1000 || 5000;

			const plugins = autoplayEnabled
				? [
						() =>
							Autoplay({
								delay,
								stopOnInteraction: false,
							}),
					]
				: [];

			applySlideSizes(viewport);

			const embla = window.ThemeCarousel.create(
				viewport,
				{
					loop: true,
					align: "start",
					duration: 25,
					slides: ".carousel__slide",
				},
				plugins,
				{
					prev: slideshow.querySelector(".slideshow__button--prev"),
					next: slideshow.querySelector(".slideshow__button--next"),
					fraction: ".slideshow__navigation",
					autoplayHover: autoplayEnabled,
					onInit: (api) => {
						applySlideSizes(viewport, api);
						api.reInit();
						applySlideSizes(viewport, api);
						slideSizeCleanup = bindSlideSizeSync(viewport, api);

						api.on("reInit", () => {
							applySlideSizes(viewport, api);
						});

						if (autoplayEnabled) {
							api.plugins()?.autoplay?.play();
						}
					},
					onDestroy: () => {
						slideSizeCleanup?.();
						slideSizeCleanup = null;
					},
				},
			);

			if (embla) {
				slideshow.classList.add("is-initialized");
			}
		});
	};

	initSlideshow(document);

	document.addEventListener("shopify:section:load", (event) => {
		initSlideshow(event.target);
	});
})();
